import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  IBusinessLine,
  IFormField,
  IQa,
  IBusinessHourData,
  IDefaultClosure,
  businessLinesHooks,
} from "@/api/businessLinesApi";

interface BusinessContextType {
  // Business Lines
  businessLines: IBusinessLine[];
  isLoading: boolean;
  selectedLineId: string | null;
  setSelectedLineId: (id: string | null) => void;
  addBusinessLine: (newLine: IBusinessLine) => void;
  refetchBusinessLines: () => void;

  // Selected Line Data
  formData: IFormField[] | null;
  qaData: IQa | null;
  workingHoursData: IBusinessHourData | null;
  isDataLoading: boolean;

  // Selected Line Status
  selectedLineStatus: string | null;
  isLineActive: boolean;
  isLineReserved: boolean;
  isLinePendingActivation: boolean;
  isLineInactive: boolean;
  canEditLine: boolean;
  canSelectLine: boolean;

  // changed Line Data
  changedFormData: IFormField[] | null;
  changedQaData: IQa | null;
  changedWorkingHoursData: IBusinessHourData | null;

  // Default form fields
  defaultFormFields: IFormField[];
  isDefaultFieldsLoading: boolean;

  // Default closures
  defaultClosures: IDefaultClosure[];
  isDefaultClosuresLoading: boolean;

  // Data update methods
  updateFormData: (data: IFormField[]) => void;
  updateQaData: (data: IQa) => void;
  updateWorkingHoursData: (data: IBusinessHourData) => void;

  // Helper methods to get current data
  getCurrentFormData: () => IFormField[] | null;
  getCurrentQaData: () => IQa | null;
  getCurrentWorkingHoursData: () => IBusinessHourData | null;

  // Reset methods
  resetFormData: () => void;
  resetQaData: () => void;
  resetWorkingHoursData: () => void;

  // Save methods
  saveFormData: () => Promise<void>;
  saveQaData: () => Promise<void>;
  saveWorkingHoursData: () => Promise<void>;

  // Change detection methods
  hasFormChanges: () => boolean;
  hasQaChanges: () => boolean;
  hasWorkingHoursChanges: () => boolean;
  hasAnyChanges: () => boolean;

  // Error states
  formError: Error | null;
  qaError: Error | null;
  workingHoursError: Error | null;
}

const BusinessContext = createContext<BusinessContextType | undefined>(
  undefined
);

interface BusinessProviderProps {
  children: ReactNode;
}

export function BusinessProvider({ children }: BusinessProviderProps) {
  // Selected Line State
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);

  // Local state for form data (to track changes)
  const [formData, setFormData] = useState<IFormField[] | null>(null);
  const [qaData, setQaData] = useState<IQa | null>(null);
  const [workingHoursData, setWorkingHoursData] =
    useState<IBusinessHourData | null>(null);
  const [changedFormData, setChangedFormData] = useState<IFormField[] | null>(
    null
  );
  const [changedQaData, setChangedQaData] = useState<IQa | null>(null);
  const [changedWorkingHoursData, setChangedWorkingHoursData] =
    useState<IBusinessHourData | null>(null);

  // React Query hooks
  const {
    data: businessLines = [],
    isLoading: businessLinesLoading,
    refetch: refetchBusinessLines,
  } = businessLinesHooks.useActiveBusinessLines();

  const {
    data: defaultFormFieldsData,
    isLoading: defaultFieldsLoading,
    error: defaultFieldsError,
  } = businessLinesHooks.useDefaultFormFields();

  const { data: defaultClosuresData, isLoading: defaultClosuresLoading } =
    businessLinesHooks.useDefaultClosures();

  const { data: businessLineData, isLoading: businessLineLoading } =
    businessLinesHooks.useBusinessLineById(selectedLineId || "", "active", {
      enabled: !!selectedLineId,
    });

  const {
    data: formDataQuery,
    isLoading: formLoading,
    error: formError,
  } = businessLinesHooks.useCallFlowForm(selectedLineId || "", {
    enabled: !!selectedLineId,
  });

  const {
    data: qaDataQuery,
    isLoading: qaLoading,
    error: qaError,
  } = businessLinesHooks.useQa(selectedLineId || "", {
    enabled: !!selectedLineId,
  });

  // Mutations
  const saveFormMutation = businessLinesHooks.useSaveCallFlowForm();
  const updateQaMutation = businessLinesHooks.useUpdateQa();
  const updateWorkingHoursMutation =
    businessLinesHooks.useUpdateBusinessHourData();

  // Derived state
  const isLoading = businessLinesLoading;
  const isDataLoading = formLoading || qaLoading || businessLineLoading;
  const defaultFormFields = defaultFormFieldsData?.default_fields || [];
  // Don't show loading if there's an error (API failed)
  const isDefaultFieldsLoading = defaultFieldsLoading && !defaultFieldsError;
  const defaultClosures = defaultClosuresData?.default_closures || [];
  const isDefaultClosuresLoading = defaultClosuresLoading;
  const workingHoursError = null; // Working hours errors are handled by the mutation

  // Selected line status and active state
  const selectedLineStatus = selectedLineId
    ? businessLines.find((line: IBusinessLine) => line.id === selectedLineId)
        ?.status || null
    : null;
  const isLineActive = selectedLineStatus === "active";
  const isLineReserved = selectedLineStatus === "reserved";
  const isLinePendingActivation = selectedLineStatus === "pending_activation";
  const isLineInactive = selectedLineStatus === "inactive";
  const canEditLine = isLineActive; // Only active lines can be edited
  const canSelectLine = selectedLineStatus !== "pending_activation"; // Only pending_activation lines cannot be selected

  // Helper function to deep compare objects
  const deepEqual = (obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return obj1 === obj2;
    if (typeof obj1 !== typeof obj2) return false;
    if (typeof obj1 !== "object") return obj1 === obj2;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (let key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  };

  // Add a new business line locally (for optimistic updates)
  const addBusinessLine = (newLine: IBusinessLine) => {
    // This will be handled by React Query's cache invalidation
    refetchBusinessLines();
    console.log("Added new business line locally:", newLine);
  };

  // Data update methods - update changed data and keep original data intact
  const updateFormData = useCallback(
    (data: IFormField[]) => {
      setChangedFormData(data);
      // Auto-clear changed data if it matches original
      if (formData && deepEqual(data, formData)) {
        setChangedFormData(null);
      }
    },
    [formData]
  );

  const updateQaData = useCallback(
    (data: IQa) => {
      setChangedQaData(data);
      // Auto-clear changed data if it matches original
      if (qaData && deepEqual(data, qaData)) {
        setChangedQaData(null);
      }
    },
    [qaData]
  );

  const updateWorkingHoursData = useCallback(
    (data: IBusinessHourData) => {
      setChangedWorkingHoursData(data);
      // Auto-clear changed data if it matches original
      if (workingHoursData && deepEqual(data, workingHoursData)) {
        setChangedWorkingHoursData(null);
      }
    },
    [workingHoursData]
  );

  // Reset methods - reset changed data to null (keep original data intact)
  const resetFormData = useCallback(() => {
    setChangedFormData(null);
  }, []);

  const resetQaData = useCallback(() => {
    setChangedQaData(null);
  }, []);

  const resetWorkingHoursData = useCallback(() => {
    setChangedWorkingHoursData(null);
  }, []);

  // Save methods using React Query mutations - save changed data
  const saveFormData = async () => {
    if (!selectedLineId || !changedFormData) {
      return;
    }

    try {
      // Process form fields: set custom field IDs to null before saving
      const processedFormFields = changedFormData.map((field) => ({
        ...field,
        id: field.id && field.id.startsWith("my-") ? null : field.id,
      }));

      const response = await saveFormMutation.mutateAsync({
        fields: processedFormFields,
        lineId: selectedLineId,
      });

      // After successful save, update original data with the response data (which has new IDs)
      if (response?.fields) {
        setFormData(response.fields);
        setChangedFormData(null);
      } else {
        // Fallback to using changed data if no response
        setFormData(changedFormData);
        setChangedFormData(null);
      }
    } catch (error) {
      console.error("BusinessContext: Error saving form data:", error);
      throw error;
    }
  };

  const saveQaData = async () => {
    if (!selectedLineId || !changedQaData) return;
    try {
      const response = await updateQaMutation.mutateAsync({
        id: selectedLineId,
        data: { qa_sections: changedQaData },
      });

      // After successful save, update original data with the response data (which has new IDs)
      if (response?.qa_sections) {
        setQaData(response.qa_sections);
        setChangedQaData(null);
        console.log(
          "Q&A data saved successfully with updated IDs:",
          response.qa_sections
        );
      } else {
        // Fallback to using changed data if no response
        setQaData(changedQaData);
        setChangedQaData(null);
        console.log("Q&A data saved successfully");
      }
    } catch (error) {
      console.error("Error saving Q&A data:", error);
      throw error;
    }
  };

  const saveWorkingHoursData = async () => {
    if (!selectedLineId || !changedWorkingHoursData) return;
    try {
      const response = await updateWorkingHoursMutation.mutateAsync({
        id: selectedLineId,
        data: changedWorkingHoursData,
      });

      // After successful save, update original data with the response data
      if (response?.business_hour_data) {
        setWorkingHoursData(response.business_hour_data);
        setChangedWorkingHoursData(null);
        console.log(
          "Working hours data saved successfully with updated data:",
          response.business_hour_data
        );
      } else {
        // Fallback to using changed data if no response
        setWorkingHoursData(changedWorkingHoursData);
        setChangedWorkingHoursData(null);
        console.log("Working hours data saved successfully");
      }
    } catch (error) {
      console.error("Error saving working hours data:", error);
      throw error;
    }
  };

  // Helper methods to get current data (changed data if available, otherwise original)
  const getCurrentFormData = useCallback((): IFormField[] | null => {
    return changedFormData || formData;
  }, [changedFormData, formData]);

  const getCurrentQaData = useCallback((): IQa | null => {
    return changedQaData || qaData;
  }, [changedQaData, qaData]);

  const getCurrentWorkingHoursData =
    useCallback((): IBusinessHourData | null => {
      return changedWorkingHoursData || workingHoursData;
    }, [changedWorkingHoursData, workingHoursData]);

  // Change detection methods - compare actual values, not just existence
  const hasFormChanges = useCallback((): boolean => {
    if (changedFormData === null) return false;
    if (formData === null) return true;
    return !deepEqual(changedFormData, formData);
  }, [changedFormData, formData]);

  const hasQaChanges = useCallback((): boolean => {
    if (changedQaData === null) return false;
    if (qaData === null) return true;
    return !deepEqual(changedQaData, qaData);
  }, [changedQaData, qaData]);

  const hasWorkingHoursChanges = useCallback((): boolean => {
    if (changedWorkingHoursData === null) return false;
    if (workingHoursData === null) return true;
    return !deepEqual(changedWorkingHoursData, workingHoursData);
  }, [changedWorkingHoursData, workingHoursData]);

  const hasAnyChanges = useCallback((): boolean => {
    return hasFormChanges() || hasQaChanges() || hasWorkingHoursChanges();
  }, [hasFormChanges, hasQaChanges, hasWorkingHoursChanges]);

  // Sync React Query data with local state
  useEffect(() => {
    if (formDataQuery?.fields) {
      setFormData(formDataQuery.fields);
    }
  }, [formDataQuery]);

  useEffect(() => {
    if (qaDataQuery?.qa_sections) {
      setQaData(qaDataQuery.qa_sections);
    }
  }, [qaDataQuery]);

  useEffect(() => {
    if (businessLineData?.business_hour_data) {
      setWorkingHoursData(businessLineData.business_hour_data);
    }
  }, [businessLineData]);

  // Clear data when no line is selected
  useEffect(() => {
    if (!selectedLineId) {
      setFormData(null);
      setQaData(null);
      setWorkingHoursData(null);
      setChangedFormData(null);
      setChangedQaData(null);
      setChangedWorkingHoursData(null);
    }
  }, [selectedLineId]);

  const value: BusinessContextType = {
    // Business Lines
    businessLines,
    isLoading,
    selectedLineId,
    setSelectedLineId,
    addBusinessLine,
    refetchBusinessLines,

    // Selected Line Data
    formData,
    qaData,
    workingHoursData,
    isDataLoading,

    // Selected Line Status
    selectedLineStatus,
    isLineActive,
    isLineReserved,
    isLinePendingActivation,
    isLineInactive,
    canEditLine,
    canSelectLine,

    // changed Line Data
    changedFormData,
    changedQaData,
    changedWorkingHoursData,

    // Default form fields
    defaultFormFields,
    isDefaultFieldsLoading,

    // Default closures
    defaultClosures,
    isDefaultClosuresLoading,

    // Data update methods
    updateFormData,
    updateQaData,
    updateWorkingHoursData,

    // Helper methods to get current data
    getCurrentFormData,
    getCurrentQaData,
    getCurrentWorkingHoursData,

    // Reset methods
    resetFormData,
    resetQaData,
    resetWorkingHoursData,

    // Save methods
    saveFormData,
    saveQaData,
    saveWorkingHoursData,

    // Change detection methods
    hasFormChanges,
    hasQaChanges,
    hasWorkingHoursChanges,
    hasAnyChanges,

    // Error states
    formError: formError as Error | null,
    qaError: qaError as Error | null,
    workingHoursError,
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

// Custom hook to use the business context
export function useBusiness() {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error("useBusiness must be used within a BusinessProvider");
  }
  return context;
}

// Export the context for direct access if needed
export { BusinessContext };
